"use client";

import { useMemo, useContext } from "react";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import type { TrackShipment } from "@/schemas/ship";
import { Button } from "@/components/ui/button";
import { StageIcon } from "./stage-icon";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { toast } from "sonner";
import { TrackerContext } from "./hero";
import { cn } from "@/lib/utils";

interface ShipmentTrackingDetailsProps {
  data: TrackShipment;
  reset: () => void;
  shipmentId: string;
}

const toDate = (value?: string) => {
  if (!value) return null;
  const normalized = value
    .replace(" UTC", "")
    .replace(" +0000", "")
    .replace(" ", "T")
    .split(".")[0];

  const date = new Date(`${normalized}Z`);
  if (Number.isNaN(date.getTime())) return new Date(value);
  return date;
};

const formatDateTime = (value?: string) => {
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return value || "-";

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export function ShipmentTrackingDetails({ data, shipmentId, reset }: ShipmentTrackingDetailsProps) {
  const { showExpandedHistory, setShowExpandedHistory } = useContext(TrackerContext);

  const history = useMemo(
    () => data.international_shipment.history || [],
    [data.international_shipment.history],
  );

  const currentIndex = useMemo(() => {
    const id = data.international_shipment.current_stage.stage_id;
    const index = history.findIndex((stage) => stage.stage_id === id);
    return index === -1 ? history.length - 1 : index;
  }, [data.international_shipment.current_stage.stage_id, history]);

  const buildShareMessage = () => {
    const stageLines =
      history.length === 0
        ? ["No tracking updates yet."]
        : history.map((stage, idx) => {
            const entered = formatDateTime(stage.entered_at);
            const exited = stage.exited_at && formatDateTime(stage.exited_at);
            const timing = exited && exited !== "-" ? `${entered} → ${exited}` : entered;

            return [
              `${idx + 1}. ${stage.title}`,
              stage.description ? `   ${stage.description}` : "",
              timing ? `   Time: ${timing}` : "",
            ]
              .filter(Boolean)
              .join("\n");
          });

    return [
      `Shipment Tracking Details`,
      `Tracking ID: ${data.shipment_id}`,
      `Origin: ${data.origin_country}`,
      `Destination: ${data.destination_country}`,
      `Customer: ${data.customer_name}`,
      `Receiver: ${data.receiver_name}`,
      ``,
      `Tracking Stages:`,
      stageLines.join("\n"),
    ]
      .filter(Boolean)
      .join("\n");
  };

  const handleShare = async () => {
    const message = buildShareMessage();

    try {
      // if the platform/ device supports sharing, share the shipment details
      if (navigator.share) {
        await navigator.share({
          title: "Shipment Tracking",
          text: message,
        });
        return;
      }

      // this is a fallback for when sharing is not allowed on the device/platform
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(message);
        toast("Tracking Details Copied");
      }
    } catch {
      // Intentionally ignore share errors to avoid blocking the UI.
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`https://proc360.app/tracker/${data.shipment_id}`);
      toast.success("Tracking link copied!");
    } catch {
      // Ignore clipboard errors (e.g. permissions).
    }
  };

  const extractInitialsFromName = (name: string) => {
    const nameArr = name.trim().split(/\s+/);
    return nameArr.reduce((acc, char) => {
      if (acc === "") return char[0];
      return acc + " " + char[0];
    }, "");
  };

  const trackAnother = {
    label: "Track another shipment",
    className:
      "text-xs sm:text-sm font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-4",
  };

  return (
    <div className="relative flex flex-col gap-6 text-primary-text-700 p-6 pb-20 lg:p-[59px] lg:pb-20">
      <div className={cn(
        "flex flex-col gap-[26px]",
        showExpandedHistory
          ? "lg:grid lg:grid-cols-2 lg:gap-12"
          : "lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-0"
      )}>
        <div className={cn(
          "flex flex-col gap-6",
          showExpandedHistory ? "lg:mt-[70px]" : "lg:my-auto"
        )}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium sm:text-2xl">
                  Tracking ID: {data.shipment_id}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full">
              <span className="font-medium text-[#664400] truncate max-w-[45%] sm:max-w-none">
                {data.origin_country}
              </span>
              <div className="flex-1 flex items-center min-w-0">
                <div className="flex-1 border-t border-dashed border-slate-300 h-0" />
                <svg
                  width="8"
                  height="10"
                  viewBox="0 0 8 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0 -ml-px"
                >
                  <path d="M0 0L8 5L0 10V0Z" fill="#000" />
                </svg>
              </div>
              <span className="font-medium text-right sm:text-left text-[#063982] truncate max-w-[45%] sm:max-w-none">
                {data.destination_country}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch mt-4">
            <div className="flex flex-col gap-2 border-l border-slate-200 pl-4">
              <p className="text-lg text-[#121212] font-medium leading-[120%]">Customer info</p>
              <div className="flex items-center gap-3">
                <Avatar className="bg-primary-600">
                  <AvatarFallback>{`${extractInitialsFromName(
                    data.customer_name,
                  )}`}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-semibold text-[#050505B2] leading-[120%]">{data.customer_name}</p>
                  <p className="text-xs text-[#323232] font-normal leading-[120%]">{data.customer_phone}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 text-sm border-l border-slate-200 pl-4">
              <p className="text-lg font-medium text-[#121212] leading-[120%]">Receiver info</p>
              <p className=" text-sm text-[#050505B2] font-semibold leading-[120%]">{data.receiver_name}</p>
              <p className="text-xs text-[#323232] font-normal leading-[120%]">{data.receiver_phone}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-center mt-6 w-full">
            <Button
              radius="full"
              size="sm"
              className="w-full bg-primary-600 text-white hover:bg-primary-700"
              onClick={handleShare}
            >
              Share track details
            </Button>
            <Button
              radius="full"
              size="sm"
              variant="outline"
              className="w-full border-[#063982] text-[#063982] bg-[#E6EBF3] hover:bg-primary-50"
              onClick={handleCopy}
            >
              Copy link
            </Button>
          </div>
        </div>

        {!showExpandedHistory && (
          <div className="hidden lg:flex flex-col items-center ml-9">
            {[0, 1, 2, 3].map((i) => {
              const isFirst = i === 0;
              const isLastDot = i === 3;
              const isCurrentDot = i === 0 && history.length > 0;
              return (
                <React.Fragment key={i}>
                  {!isFirst && <div className="w-px flex-1 bg-slate-200" />}
                  <span
                    className={cn(
                      "w-2.5 h-2.5 rounded-full shrink-0 transition-colors",
                      isCurrentDot
                        ? "bg-[#2EC66F] ring-4 ring-[#2EC66F]/25"
                        : "bg-slate-200"
                    )}
                  />
                  {!isLastDot && <div className="w-px flex-1 bg-slate-200" />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div
          className={cn(
            "flex flex-col mt-0 lg:mt-0",
            showExpandedHistory
              ? "order-none gap-5 mb-0"
              : "order-first mb-0 lg:order-none lg:mb-0",
            !showExpandedHistory && data.overall_status === "arrived" ? "gap-2" : "gap-5"
          )}
        >
          {data.overall_status === "arrived" && !showExpandedHistory ? (
            <div className="flex flex-col items-center text-center p-6 min-h-[400px] h-full w-full">
              <div className="w-[73px] h-[73px] rounded-full bg-[#C7F1E6] flex items-center justify-center">
                <Image
                  src="/tracking/arrived.png"
                  width={42}
                  height={42}
                  alt="Arrived"
                  className="object-contain"
                />
              </div>
              <h3 className="mt-4 text-2xl lg:text-xl font-medium text-black text-center">
                Shipment Arrived
              </h3>
              <p className="mt-2 text-lg leading-[120%] text-[#050505B2] font-medium text-center max-w-sm mx-auto">
                Your shipment has arrived! Please check your email for the next steps.
              </p>
              <button
                type="button"
                className="mt-6 text-xs font-normal text-[#063982] hover:text-[#38619B] underline underline-offset-4 transition-colors"
                onClick={() => setShowExpandedHistory(true)}
              >
                Check tracking history
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-start mb-2">
                <div className="inline-flex items-center gap-1 bg-[#003380] p-1 rounded-full">
                  <button
                    type="button"
                    className="bg-white text-[#003380] px-5 py-1.5 rounded-full text-sm font-semibold shadow-sm transition-all"
                  >
                    International Delivery
                  </button>
                  <button
                    type="button"
                    disabled
                    className="text-white/80 px-5 py-1.5 text-sm font-medium transition-colors pointer-events-none opacity-60 cursor-not-allowed"
                  >
                    Last Mile Delivery
                  </button>
                </div>
              </div>

              {(() => {
                const sorted = [...history].sort(
                  (a, b) => (toDate(b.entered_at)?.getTime() ?? 0) - (toDate(a.entered_at)?.getTime() ?? 0)
                );
                return (
                  <div className="flex flex-col">
                    {sorted.map((stage, index) => {
                      const isFirst = index === 0;
                      const isLast = index === sorted.length - 1;
                      const isActive = index === 0;

                      return (
                        <div key={stage.stage_id} className="flex gap-3">
                          <div className="flex flex-col items-center shrink-0 w-2.5">
                            {!isFirst && (
                              <div className="w-px bg-slate-200 h-[25px]" />
                            )}
                            {isFirst && (
                              <div className="h-[25px]" />
                            )}
                            <span
                              className={cn(
                                "w-2.5 h-2.5 rounded-full shrink-0 transition-colors",
                                isFirst
                                  ? "bg-[#2EC66F] ring-4 ring-[#2EC66F]/25"
                                  : "bg-slate-200"
                              )}
                            />
                            {!isLast && (
                              <div className="w-px flex-1 bg-slate-200" />
                            )}
                          </div>
                          <div className={cn("flex-1 flex items-start gap-3 pt-[10px]", !isLast && "pb-5")}>
                            <div className="w-10 h-10 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full bg-slate-50 shrink-0">
                              <StageIcon
                                iconfile={stage.icon_file}
                                title={stage.title}
                                isActive={isActive}
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <p className="text-sm font-semibold text-primary-text-700">{stage.title}</p>
                              <p className="text-xs text-primary-text-400">{stage.description}</p>
                              <div className="flex flex-wrap gap-3 text-[11px] text-primary-text-300">
                                <span>{formatDateTime(stage.entered_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex justify-center">
        {shipmentId.trim().length === 0 ? (
          <Button onClick={reset} variant="link" className={trackAnother.className}>
            {trackAnother.label}
          </Button>
        ) : (
          <Link href="/tracker" className={trackAnother.className}>
            {trackAnother.label}
          </Link>
        )}
      </div>
    </div>
  );
}
